import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button } from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import {
  Wallet,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Info,
} from "lucide-react";
import { companyApi } from "@/services/api";

interface WalletData {
  address: string;
  networkId: string;
  networkName: string;
  explorerUrl: string;
  balance: string;
  lastSyncedAt?: string;
}

export function CompanyWalletPage() {
  const [walletData, setWalletData] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadWallet = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await companyApi.dashboard.getWallet();
      setWalletData(data);
    } catch (err: any) {
      setError(err.message || "Erro ao carregar carteira");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWallet();
  }, []);

  const handleCopy = async () => {
    if (!walletData?.address) return;
    try {
      await navigator.clipboard.writeText(walletData.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };

  const handleOpenExplorer = () => {
    if (!walletData?.explorerUrl || !walletData?.address) return;
    window.open(
      `${walletData.explorerUrl}/address/${walletData.address}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "Nunca";
    const date = new Date(dateStr);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Wallet className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Carteira Blockchain"
        description="Sua carteira de tokens BRLD"
      />

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <Card className="border-destructive/20 bg-destructive/5">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-destructive" />
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={loadWallet} className="ml-auto">
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      )}

      {walletData && !loading && (
        <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2">
          {/* Card principal - Endereço */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5" />
                Endereço da Carteira
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Endereço */}
              <div className="flex items-center gap-2 p-4 bg-muted rounded-lg">
                <code className="flex-1 font-mono text-sm break-all">
                  {walletData.address}
                </code>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleCopy}
                    className="p-2 hover:bg-muted-foreground/10 rounded-lg transition-colors"
                    title="Copiar endereço"
                  >
                    {copied ? (
                      <Check className="w-5 h-5 text-success" />
                    ) : (
                      <Copy className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  <button
                    onClick={handleOpenExplorer}
                    className="p-2 hover:bg-muted-foreground/10 rounded-lg transition-colors"
                    title="Ver no explorer"
                  >
                    <ExternalLink className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
              </div>

              {/* Informações da rede */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Rede</p>
                  <p className="font-medium">{walletData.networkName || walletData.networkId}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Última sincronização</p>
                  <p className="font-medium">{formatDate(walletData.lastSyncedAt)}</p>
                </div>
              </div>

              {/* Botão ver no explorer */}
              <Button variant="outline" className="w-full" onClick={handleOpenExplorer}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver no Explorer ({walletData.networkName || "Blockchain"})
              </Button>
            </CardContent>
          </Card>

          {/* Card de saldo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Saldo BRLD
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl sm:text-3xl font-bold">{walletData.balance} BRLD</p>
              <p className="text-sm text-muted-foreground mt-1">
                ≈ R$ {parseFloat(walletData.balance).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </CardContent>
          </Card>

          {/* Card informativo */}
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary text-sm">
                <Info className="w-4 h-4" />
                Sobre sua carteira
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>
                Sua carteira blockchain é gerenciada de forma segura pela Liqi.
              </p>
              <p>
                Tokens BRLD são emitidos automaticamente após a confirmação do seu
                depósito PIX.
              </p>
              <p>
                Você pode acompanhar todas as transações através do explorer.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
